import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { GlobeIcon, PlusIcon, SearchIcon, SparklesIcon, XIcon } from 'lucide-react';
import { useAccount } from '../../contexts/AccountContext';
import { Panel } from '../../components/ui/Panel';
import { Button } from '../../components/ui/Button';
import { TextInput } from '../../components/ui/TextInput';
import { SelectInput } from '../../components/ui/SelectInput';
import { MonitorRow } from '../../components/MonitorRow';
import { AttentionPanel } from '../../components/AttentionPanel';
import { EmptyState } from '../../components/EmptyState';
import { certificateDaysRemaining, overallStatus, statusRank } from '../../utils/health';
type SortKey = 'expiry' | 'status' | 'domain';
type FilterKey = 'all' | 'attention' | 'healthy' | 'ssl' | 'uptime';
function RowSkeleton() {
  return <li className="flex items-center gap-4 border-b border-line px-5 py-4 last:border-b-0" data-id="element-887" data-mp="pk17a9erbzth">
      <div className="min-w-0 flex-1 space-y-2" data-id="element-888" data-mp="zd4elw26jm2j">
        <div className="skeleton h-3.5 w-40" data-id="element-889" data-mp="zefgtrfz8lrj" />
        <div className="skeleton h-2.5 w-24" data-id="element-890" data-mp="13epmf2zuxa5v" />
      </div>
      <div className="skeleton h-5 w-24 rounded-full" data-id="element-891" data-mp="13fiuc7ajy5pl" />
      <div className="skeleton h-6 w-32" data-id="element-892" data-mp="18e2jshoctzmp" />
    </li>;
}
export function Dashboard() {
  const {
    monitors,
    toggleCheck,
    servicesUsed,
    serviceLimit,
    plan,
    justSubscribed,
    dismissSubscribedBanner
  } = useAccount();
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sort, setSort] = useState<SortKey>('expiry');
  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 550);
    return () => window.clearTimeout(timer);
  }, []);
  const atLimit = servicesUsed >= serviceLimit;
  const visible = useMemo(() => {
    const filtered = monitors.filter(monitor => {
      const matchesQuery = query.trim() === '' || monitor.domain.toLowerCase().includes(query.trim().toLowerCase()) || monitor.label.toLowerCase().includes(query.trim().toLowerCase());
      if (!matchesQuery) {
        return false;
      }
      const status = overallStatus(monitor);
      if (filter === 'attention') {
        return status === 'warning' || status === 'critical';
      }
      if (filter === 'healthy') {
        return status === 'healthy';
      }
      if (filter === 'ssl') {
        return monitor.sslEnabled;
      }
      if (filter === 'uptime') {
        return monitor.uptimeEnabled;
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      if (sort === 'domain') {
        return a.domain.localeCompare(b.domain);
      }
      if (sort === 'status') {
        return statusRank(overallStatus(b)) - statusRank(overallStatus(a));
      }
      const aDays = certificateDaysRemaining(a) ?? Number.MAX_SAFE_INTEGER;
      const bDays = certificateDaysRemaining(b) ?? Number.MAX_SAFE_INTEGER;
      return aDays - bDays;
    });
  }, [monitors, query, filter, sort]);
  return <div className="space-y-5" data-id="element-893" data-mp="2ld3vwnotg32i">
      {justSubscribed && <div className="flex items-start gap-3 rounded-card border border-brand-border bg-brand-soft px-5 py-4" data-id="element-894" data-mp="2qadqehr2b4x7">
          <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-895" data-mp="2bdcxwc11r5xo" />
          <div className="min-w-0 flex-1" data-id="element-896" data-mp="2gfc0vksvk0u3">
            <p className="text-[13px] font-semibold text-brand-ink" data-id="element-897" data-mp="22lb3audnsjvb">
              You are on {plan.name} — {serviceLimit} services available
            </p>
            <p className="mt-1 text-[13px] leading-relaxed text-brand-ink/85" data-id="element-898" data-mp="27enkijg0doe1">
              Your first domain is queued for its opening check. Add the rest of
              your sites now, or set your alert recipients first.
            </p>
          </div>
          <button type="button" onClick={dismissSubscribedBanner} aria-label="Dismiss" className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-control text-brand-ink transition-colors duration-150 ease-snap hover:bg-brand-border/40" data-id="element-899" data-mp="1jo1fz257qm8s">
            <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-900" data-mp="1bmsqcbcfkyfr" />
          </button>
        </div>}

      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-901" data-mp="16tzmdposdu4q">
        <div data-id="element-902" data-mp="11x936thoqif3">
          <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-903" data-mp="11vpqtdz5gril">
            Monitors
          </h1>
          <p className="tabular mt-1 text-[13px] text-ink-muted" data-id="element-904" data-mp="xwguq2gn0hjv">
            {monitors.length} domains · {servicesUsed} of {serviceLimit}{' '}
            services in use
          </p>
        </div>
        <Button to="/app/monitors/new" disabled={atLimit} data-id="element-905" data-mp="nx849jkzdl29">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-906" data-mp="iy4y1vt9l8sp" />
          Add a domain
        </Button>
      </div>

      {!loading && monitors.length > 0 && <AttentionPanel monitors={monitors} data-id="element-907" data-mp="2i9xyk2f9gwa6" />}

      {monitors.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-908" data-mp="2dcxrppal7pi0">
          <div className="relative min-w-[200px] flex-1" data-id="element-909" data-mp="2lsmzhvxqu4xs">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" aria-hidden="true" data-id="element-910" data-mp="2ls56r9aai8um" />
            <label htmlFor="monitor-search" className="sr-only" data-id="element-911" data-mp="2um043v566uzp">
              Search monitors
            </label>
            <TextInput id="monitor-search" placeholder="Search domains" value={query} onChange={event => setQuery(event.target.value)} className="pl-9" data-id="element-912" data-mp="2zjjrbkjuii3s" />
          </div>

          <div className="w-[168px]" data-id="element-913" data-mp="2buqr3bw6xo3d">
            <label htmlFor="monitor-filter" className="sr-only" data-id="element-914" data-mp="2btpide2nddot">
              Filter monitors
            </label>
            <SelectInput id="monitor-filter" value={filter} onChange={event => setFilter(event.target.value as FilterKey)} options={[{
          value: 'all',
          label: 'All monitors'
        }, {
          value: 'attention',
          label: 'Needs attention'
        }, {
          value: 'healthy',
          label: 'Healthy only'
        }, {
          value: 'ssl',
          label: 'SSL enabled'
        }, {
          value: 'uptime',
          label: 'Uptime enabled'
        }]} data-id="element-915" data-mp="2guv9j7gnssw8" />
          </div>

          <div className="w-[168px]" data-id="element-916" data-mp="37jxws5o4ewdb">
            <label htmlFor="monitor-sort" className="sr-only" data-id="element-917" data-mp="37kzhz2yuoz8r">
              Sort monitors
            </label>
            <SelectInput id="monitor-sort" value={sort} onChange={event => setSort(event.target.value as SortKey)} options={[{
          value: 'expiry',
          label: 'Soonest expiry'
        }, {
          value: 'status',
          label: 'Worst status'
        }, {
          value: 'domain',
          label: 'Domain A–Z'
        }]} data-id="element-918" data-mp="2julmf2h24i9f" />
          </div>
        </div>}

      <Panel as="section" className="overflow-hidden" data-id="element-919" data-mp="2119hftnd785q">
        {loading ? <ul data-id="element-920" data-mp="1w49azhmls3e3">
            <RowSkeleton data-id="element-921" data-mp="1w2ongbyphkpq" />
            <RowSkeleton data-id="element-922" data-mp="blyqnlpdg7at" />
            <RowSkeleton data-id="element-923" data-mp="bloxjp915k0w" />
            <RowSkeleton data-id="element-924" data-mp="bl5nsvtja1xy" />
          </ul> : monitors.length === 0 ? <EmptyState icon={GlobeIcon} title="No domains yet" body="Add the first site you would hate to find broken. You can switch on certificate expiry, uptime, or both." action={<Button to="/app/monitors/new" data-id="element-926" data-mp="6l17b5sml77q">
                <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-927" data-mp="udzvr5sqotso" />
                Add a domain
              </Button>} data-id="element-925" data-mp="gdytblwtteb3" /> : visible.length === 0 ? <EmptyState icon={SearchIcon} title="Nothing matches that" body="No monitor matches your search and filter together. Try clearing one of them." action={<Button variant="secondary" onClick={() => {
        setQuery('');
        setFilter('all');
      }} data-id="element-929" data-mp="36298rzgp1lsr">
                Clear search and filter
              </Button>} data-id="element-928" data-mp="lctcwq2mb6w9" /> : <>
            <div className="hidden border-b border-line bg-canvas px-5 py-2.5 md:grid md:grid-cols-[1.6fr_1.1fr_1.3fr_auto] md:gap-4" data-id="element-930" data-mp="m587h2unyfq">
              <span className="pl-3 text-[11px] font-semibold uppercase tracking-wide text-ink-soft" data-id="element-931" data-mp="3st3flwiec8ls">
                Domain
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-soft" data-id="element-932" data-mp="odary7bwvavb">
                Certificate
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-soft" data-id="element-933" data-mp="kbz68cw5r9se">
                Uptime
              </span>
              <span className="w-8" aria-hidden="true" data-id="element-934" data-mp="almb5qqpvy80" />
            </div>
            <ul data-id="element-935" data-mp="ajjpfiwlkr93">
              {visible.map(monitor => <MonitorRow key={monitor.id} monitor={monitor} atLimit={atLimit} onEnable={kind => toggleCheck(monitor.id, kind)} data-id="element-936" data-mp="181jr51bvgh4b" />)}
            </ul>
          </>}
      </Panel>

      {atLimit && monitors.length > 0 && <p className="text-[13px] text-ink-muted" data-id="element-937" data-mp="1fm45gxpk7tm2">
          {plan.name} is using all {serviceLimit} services. Switch a check off
          to free one up, or{' '}
          <Link to="/app/billing" className="font-medium text-brand underline decoration-1 underline-offset-2 hover:text-brand-hover" data-id="element-938" data-mp="1olqv6m8xfaza">
            move up a plan
          </Link>
          .
        </p>}
    </div>;
}